import * as React from "react"
import {
  type Column,
  type ColumnDef,
  type ColumnPinningState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui"

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    align?: "left" | "right"
    verticalAlign?: "top" | "middle"
  }
}
import { useTranslation } from "react-i18next"
import type { RowJobStatus } from "@nexus/contracts"
import type { Opportunity, OpportunityTabKey } from "../../../types/opportunities.types"
import { HighlightText } from "../../HighlightText"
import { OpportunityStatusBadge } from "../OpportunityStatusBadge/OpportunityStatusBadge"
import { OpportunityActionBadge } from "../OpportunityActionBadge/OpportunityActionBadge"
import { RowJobStatusBadge } from "../RowJobStatusBadge/RowJobStatusBadge"
import {
  OpportunityRowDragContext,
  OpportunityRowDragHandle,
} from "../../opportunity-row/OpportunityRowDrag"
import { formatOpportunityEngineLabel, formatPct, formatPercent } from "../../../lib/format-opportunity"
import { DualSpendValueWithTooltip } from "../../DualSpendValue"
import { OpportunitiesTableFullSkeleton } from "./OpportunitiesTableSkeletonRows"
import {
  readStoredOpportunityRowOrder,
  sortByStoredOpportunityIds,
  writeStoredOpportunityRowOrder,
} from "../../../lib/opportunity-row-order"

export type TableDensity = "condensed" | "regular" | "relaxed"

export interface OpportunitiesTableProps {
  density?: TableDensity
  /** Scoped drag-order persistence (with {@link setId}) between table and card views. */
  tenantId: string
  setId: string
  data: Opportunity[]
  tabKey: OpportunityTabKey
  isLoading?: boolean
  /** When true and `data.length > 0`, skeleton rows append inside the same table body (infinite scroll). */
  isFetchingNextPage?: boolean
  /** Rows to show while fetching the next page. Default 5. */
  nextPageSkeletonRowCount?: number
  hasActiveFilters?: boolean
  onClearFilters?: () => void
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void
  onRowClick?: (opportunity: Opportunity) => void
  jobItems?: Record<string, RowJobStatus>
  onRetryRow?: (opportunityId: string) => void
  search?: string
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (state: VisibilityState) => void
  columnOrder?: string[]
  onColumnOrderChange?: (order: string[]) => void
  onColumnsReady?: (columns: Column<Opportunity, unknown>[]) => void
  sorting?: SortingState
  onSortingChange?: (state: SortingState) => void
  /** Fired after a completed row drag so the page can hide column-sort UI without refetching. */
  onManualRowReorder?: () => void
  columnPinning?: ColumnPinningState
  onColumnPinningChange?: (state: ColumnPinningState) => void
  industryLabelByIndex?: Map<string, string>
  /** Enables j/k row navigation and Space/Enter actions (table view only). */
  enableKeyboardShortcuts?: boolean
}

const fmt = {
  pct: formatPct,
  pctAbs: formatPercent,
}

const OPPORTUNITY_TABLE_DRAG_TYPE = {
  column: "opportunity-table-column",
  row: "opportunity-table-row",
} as const

function DraggableTableHead({
  id,
  disabled,
  children,
  style: styleProp,
  ...thProps
}: React.ComponentProps<typeof TableHead> & { id: string; disabled?: boolean }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled, data: { type: OPPORTUNITY_TABLE_DRAG_TYPE.column } })

  const style: React.CSSProperties = {
    ...styleProp,
    transform: CSS.Transform.toString(transform ? { ...transform, y: 0, scaleX: 1, scaleY: 1 } : null),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <TableHead ref={setNodeRef} style={style} {...attributes} {...thProps} {...(disabled ? {} : listeners)}>
      {children}
    </TableHead>
  )
}

/** Tooltip wrapper for truncated/clamped cell text. Only shows tooltip when text is long enough to be clipped. */
function CellTooltip({ text, children }: { text: string; children: React.ReactNode }) {
  if (text.length < 60) return <>{children}</>
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-sm">
        {text}
      </TooltipContent>
    </Tooltip>
  )
}

function SortableRow({
  id,
  ri,
  isSelected,
  isFocused,
  onClick,
  onKeyDown,
  rowRef,
  children,
}: {
  id: string
  ri: number
  isSelected: boolean
  isFocused?: boolean
  onClick: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => void
  rowRef?: (el: HTMLTableRowElement | null) => void
  children: React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { type: OPPORTUNITY_TABLE_DRAG_TYPE.row } })

  const dragCtx = React.useMemo(() => ({ listeners, attributes }), [listeners, attributes])

  return (
    <OpportunityRowDragContext value={dragCtx}>
      <tr
        ref={(el) => {
          setNodeRef(el)
          rowRef?.(el)
        }}
        data-slot="table-row"
        data-state={isSelected ? "selected" : undefined}
        aria-current={isFocused ? "true" : undefined}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.4 : undefined,
          zIndex: isDragging ? 10 : undefined,
          animationDuration: "200ms",
          animationDelay: `${Math.min(ri, 20) * 30}ms`,
        }}
        className={cn(
          "group/row border-b border-border-subtle transition-colors hover:bg-muted/40 data-[state=selected]:bg-primary/[0.06] data-[state=selected]:hover:bg-primary/[0.08] cursor-pointer motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both motion-reduce:animate-none",
          isFocused && "bg-foreground/[0.04]",
        )}
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {children}
      </tr>
    </OpportunityRowDragContext>
  )
}

export function OpportunitiesTable({
  density = "regular",
  tenantId,
  setId,
  data,
  tabKey,
  isLoading,
  isFetchingNextPage,
  nextPageSkeletonRowCount = 5,
  hasActiveFilters,
  onClearFilters,
  rowSelection = {},
  onRowSelectionChange,
  onRowClick,
  jobItems,
  onRetryRow,
  search = "",
  columnVisibility = {},
  onColumnVisibilityChange,
  columnOrder = [],
  onColumnOrderChange,
  onColumnsReady,
  sorting: sortingProp,
  onSortingChange: onSortingChangeProp,
  onManualRowReorder,
  columnPinning: columnPinningProp,
  onColumnPinningChange,
  industryLabelByIndex,
  enableKeyboardShortcuts = false,
}: OpportunitiesTableProps) {
  const { t } = useTranslation()
  const [sortingLocal, setSortingLocal] = React.useState<SortingState>([])
  const sorting = sortingProp ?? sortingLocal
  const setSorting = onSortingChangeProp ?? setSortingLocal

  const DEFAULT_PINNING: ColumnPinningState = { left: ["select"] }
  const [columnPinningLocal, setColumnPinningLocal] = React.useState<ColumnPinningState>(DEFAULT_PINNING)
  const columnPinning = columnPinningProp ?? columnPinningLocal
  const setColumnPinning = onColumnPinningChange ?? setColumnPinningLocal

  // Column and row reorder DnD. Keep one DndContext outside the table so
  // dnd-kit accessibility nodes never render inside tbody.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const [rowOrder, setRowOrder] = React.useState<string[]>(() =>
    readStoredOpportunityRowOrder(tenantId, setId),
  )

  React.useEffect(() => {
    setRowOrder(readStoredOpportunityRowOrder(tenantId, setId))
  }, [tenantId, setId])

  const selectColumn: ColumnDef<Opportunity> = React.useMemo(
    () => ({
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t("opportunities.table.selectAll")}
        />
      ),
      cell: ({ row }) => (
        <div className="relative flex items-center justify-center">
          <OpportunityRowDragHandle />
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={t("opportunities.table.selectRow")}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "transition-opacity duration-150",
              row.getIsSelected() ? "opacity-100" : "opacity-0 group-hover/row:opacity-100",
            )}
          />
        </div>
      ),
      size: 52,
      minSize: 52,
      enableSorting: false,
      enableResizing: false,
    }),
    [t],
  )

  const sharedDataColumns: ColumnDef<Opportunity>[] = React.useMemo(
    () => [
      // ── Primary columns ──────────────────────────────────────────────
      {
        accessorKey: "flowName",
        header: () => t("opportunities.table.flowName"),
        cell: ({ row }) => (
          <CellTooltip text={row.original.flowName}>
            <span className="block truncate text-sm text-muted-foreground">
              {row.original.flowName}
            </span>
          </CellTooltip>
        ),
        size: 150,
        minSize: 100,
      },
      {
        accessorKey: "topicTitle",
        header: () => t("opportunities.table.topicTitle"),
        cell: ({ row }) => (
          <CellTooltip text={row.original.topicTitle}>
            <span className="block truncate font-medium text-foreground">
              <HighlightText text={row.original.topicTitle} query={search} />
            </span>
          </CellTooltip>
        ),
        size: 280,
        minSize: 160,
      },
      {
        accessorKey: "topicDescription",
        header: () => t("opportunities.table.topicDescription"),
        cell: ({ row }) => (
          <CellTooltip text={row.original.topicDescription}>
            <span className="line-clamp-2 whitespace-normal text-sm text-muted-foreground">
              <HighlightText text={row.original.topicDescription} query={search} />
            </span>
          </CellTooltip>
        ),
        size: 360,
        minSize: 200,
        meta: { verticalAlign: "top" },
      },
      {
        accessorKey: "findings",
        header: () => t("opportunities.table.findings"),
        cell: ({ row }) => (
          <CellTooltip text={row.original.findings}>
            <span className="block truncate text-sm text-muted-foreground">{row.original.findings}</span>
          </CellTooltip>
        ),
        size: 180,
        minSize: 120,
        sortDescFirst: true,
      },
      {
        accessorKey: "action",
        header: () => t("opportunities.table.action"),
        cell: ({ row }) => <OpportunityActionBadge action={row.original.action} />,
        size: 120,
        minSize: 90,
      },
      {
        accessorKey: "suggestion",
        header: () => t("opportunities.table.suggestion"),
        cell: ({ row }) => (
          <CellTooltip text={row.original.suggestion}>
            <span className="line-clamp-2 whitespace-normal text-sm text-muted-foreground">
              <HighlightText text={row.original.suggestion} query={search} />
            </span>
          </CellTooltip>
        ),
        size: 360,
        minSize: 200,
        meta: { verticalAlign: "top" },
      },
      // ── Secondary columns (visible via horizontal scroll) ────────────
      {
        accessorKey: "spend",
        header: () => t("opportunities.table.spend"),
        cell: ({ row }) => (
          <DualSpendValueWithTooltip
            perDataset={row.original.spendPerDataset}
            totalCreatives={row.original.spend}
          />
        ),
        size: 140,
        minSize: 110,
        enableResizing: false,
        meta: { align: "right" },
      },
      {
        accessorKey: "benchmarkSpend",
        header: () => t("opportunities.table.benchmarkSpend"),
        cell: ({ row }) => (
          <DualSpendValueWithTooltip
            perDataset={row.original.benchmarkSpendPerDataset}
            totalCreatives={row.original.benchmarkSpend}
          />
        ),
        size: 200,
        minSize: 150,
        enableResizing: false,
        meta: { align: "right" },
      },
      {
        accessorKey: "competitorSpend",
        header: () => t("opportunities.table.competitorSpend"),
        cell: ({ row }) => (
          <DualSpendValueWithTooltip
            perDataset={row.original.competitorSpendPerDataset}
            totalCreatives={row.original.competitorSpend}
          />
        ),
        size: 200,
        minSize: 150,
        enableResizing: false,
        meta: { align: "right" },
      },
      {
        accessorKey: "feature",
        header: () => t("opportunities.table.feature"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.feature}</span>
        ),
        size: 120,
        minSize: 80,
        enableResizing: false,
      },
      {
        accessorKey: "subFeature",
        header: () => t("opportunities.table.subFeature"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.subFeature}</span>
        ),
        size: 160,
        minSize: 120,
        enableResizing: false,
      },
      {
        accessorKey: "kpi",
        header: () => t("opportunities.table.kpi"),
        cell: ({ row }) => {
          const v = row.original.kpi
          return (
            <span className="font-mono tabular-nums text-sm text-muted-foreground">
              {fmt.pct(v)}
            </span>
          )
        },
        size: 90,
        minSize: 70,
        enableResizing: false,
        meta: { align: "right" },
      },
      {
        accessorKey: "competitorKpi",
        header: () => t("opportunities.table.competitorKpi"),
        cell: ({ row }) => {
          const v = row.original.competitorKpi
          return (
            <span className="font-mono tabular-nums text-sm text-muted-foreground">
              {fmt.pct(v)}
            </span>
          )
        },
        size: 180,
        minSize: 130,
        enableResizing: false,
        meta: { align: "right" },
      },
      {
        accessorKey: "benchmarkKpi",
        header: () => t("opportunities.table.benchmarkKpi"),
        cell: ({ row }) => {
          const v = row.original.benchmarkKpi
          return (
            <span className="font-mono tabular-nums text-sm text-muted-foreground">
              {fmt.pct(v)}
            </span>
          )
        },
        size: 175,
        minSize: 130,
        enableResizing: false,
        meta: { align: "right" },
      },
      {
        accessorKey: "engine",
        header: () => t("opportunities.table.engine"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {formatOpportunityEngineLabel(row.original.engine, t)}
          </span>
        ),
        size: 160,
        minSize: 100,
        enableResizing: false,
      },
      {
        accessorKey: "industry",
        header: () => t("opportunities.table.industry"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {industryLabelByIndex?.get(row.original.industry) ?? row.original.industry}
          </span>
        ),
        size: 140,
        minSize: 100,
        enableResizing: false,
      },
      {
        accessorKey: "runDate",
        header: () => t("opportunities.table.runDate"),
        cell: ({ row }) => {
          const v = row.original.runDate
          if (!v) return <span className="text-sm text-muted-foreground/40">—</span>
          const formatted = new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
          return <span className="text-sm text-muted-foreground">{formatted}</span>
        },
        size: 130,
        minSize: 100,
        enableResizing: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, search, industryLabelByIndex],
  )

  const rejectionReasonColumn: ColumnDef<Opportunity> = React.useMemo(
    () => ({
      accessorKey: "rejectionReason",
      header: () => t("opportunities.table.rejectionReason"),
      cell: ({ row }) => {
        const reason = row.original.rejectionReason
        if (!reason) {
          return <span className="text-sm text-muted-foreground/40">—</span>
        }
        return (
          <span className="block rounded px-2 py-1 bg-warning-subtle line-clamp-2 whitespace-normal text-sm text-warning-subtle-foreground">
            {reason}
          </span>
        )
      },
      size: 260,
      minSize: 160,
      enableResizing: false,
      meta: { verticalAlign: "top" },
    }),
    [t],
  )

  const columns: ColumnDef<Opportunity>[] = React.useMemo(() => {
    const isActive = tabKey === "active" || tabKey === "rejected"

    const statusColumn: ColumnDef<Opportunity> = {
      accessorKey: "status",
      header: () => t("opportunities.table.status"),
      cell: ({ row }) => <OpportunityStatusBadge status={row.original.status} />,
      size: 120,
      minSize: 90,
    }

    const jobStatusColumn: ColumnDef<Opportunity>[] = isActive && jobItems
      ? [{
          id: "jobStatus",
          header: () => t("opportunities.table.jobStatus"),
          cell: ({ row }: { row: { original: Opportunity } }) => {
            const rowStatus = jobItems[row.original.id]
            if (!rowStatus) return null
            return <RowJobStatusBadge status={rowStatus} onRetry={() => onRetryRow?.(row.original.id)} />
          },
          size: 130,
          minSize: 100,
          enableSorting: false,
        } satisfies ColumnDef<Opportunity>]
      : []

    const rejectedColumns: ColumnDef<Opportunity>[] = tabKey === "rejected" ? [rejectionReasonColumn] : []

    return [selectColumn, statusColumn, ...sharedDataColumns, ...rejectedColumns, ...jobStatusColumn]
  }, [selectColumn, sharedDataColumns, rejectionReasonColumn, tabKey, t, jobItems, onRetryRow])

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      sorting,
      columnVisibility,
      columnPinning,
      ...(columnOrder.length > 0 ? { columnOrder } : {}),
    },
    onRowSelectionChange: onRowSelectionChange
      ? (updater) => {
          const next = typeof updater === "function" ? updater(rowSelection) : updater
          onRowSelectionChange(next)
        }
      : undefined,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater
      if (next.length > 0) {
        writeStoredOpportunityRowOrder(tenantId, setId, [])
        setRowOrder([])
      }
      setSorting(next)
    },
    onColumnVisibilityChange: onColumnVisibilityChange
      ? (updater) => {
          const next = typeof updater === "function" ? updater(columnVisibility) : updater
          onColumnVisibilityChange(next)
        }
      : undefined,
    onColumnPinningChange: (updater) => {
      const next = typeof updater === "function" ? updater(columnPinning) : updater
      setColumnPinning(next)
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    // Header clicks only flip direction. Clearing sort refetched default id-desc
    // and looked like N/A rows jumping to the top.
    enableSortingRemoval: false,
    getRowId: (row) => row.id,
    enableRowSelection: true,
    columnResizeMode: "onChange",
  })

  const leafColumns = table.getAllLeafColumns()
  const leafColumnIds = leafColumns.map((c) => c.id).join(",")
  React.useEffect(() => {
    onColumnsReady?.(table.getAllLeafColumns())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafColumnIds, onColumnsReady])

  const rawRows = table.getRowModel().rows

  const sortedRows = React.useMemo(
    () => (sorting.length > 0 ? rawRows : sortByStoredOpportunityIds(rawRows, rowOrder)),
    [rawRows, rowOrder, sorting],
  )

  const sortedRowIds = React.useMemo(() => sortedRows.map((r) => r.id), [sortedRows])

  const [focusedRowId, setFocusedRowId] = React.useState<string | null>(null)
  const rowRefs = React.useRef<Map<string, HTMLTableRowElement>>(new Map())

  React.useEffect(() => {
    setFocusedRowId(null)
    rowRefs.current.clear()
  }, [tenantId, setId, tabKey, sortedRowIds.join(",")])

  const focusedIndex = focusedRowId ? sortedRowIds.indexOf(focusedRowId) : -1

  const moveFocus = React.useCallback(
    (delta: number) => {
      if (sortedRowIds.length === 0) return
      const currentIdx = focusedRowId ? sortedRowIds.indexOf(focusedRowId) : -1
      const start = currentIdx < 0 ? (delta > 0 ? 0 : sortedRowIds.length - 1) : currentIdx
      const nextIdx = Math.max(0, Math.min(sortedRowIds.length - 1, start + delta))
      setFocusedRowId(sortedRowIds[nextIdx] ?? null)
    },
    [focusedRowId, sortedRowIds],
  )

  const toggleFocusedRow = React.useCallback(
    (rowId: string) => {
      if (!onRowSelectionChange) return
      const next = { ...rowSelection }
      if (next[rowId]) delete next[rowId]
      else next[rowId] = true
      onRowSelectionChange(next)
    },
    [onRowSelectionChange, rowSelection],
  )

  React.useEffect(() => {
    if (!focusedRowId) return
    rowRefs.current.get(focusedRowId)?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [focusedRowId])

  React.useEffect(() => {
    if (!enableKeyboardShortcuts) return

    function isEditableTarget() {
      const ae = document.activeElement
      if (!ae) return false
      const tag = ae.tagName
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (ae as HTMLElement).isContentEditable
      )
    }

    function onKeyDown(e: KeyboardEvent) {
      if (isEditableTarget()) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      switch (e.key) {
        case "j":
        case "ArrowDown": {
          if (e.shiftKey) return
          e.preventDefault()
          moveFocus(1)
          break
        }
        case "k":
        case "ArrowUp": {
          if (e.shiftKey) return
          e.preventDefault()
          moveFocus(-1)
          break
        }
        case " ": {
          if (focusedIndex < 0) return
          const rowId = sortedRowIds[focusedIndex]
          if (!rowId) return
          e.preventDefault()
          toggleFocusedRow(rowId)
          break
        }
        case "Enter": {
          if (focusedIndex < 0) return
          const row = sortedRows[focusedIndex]
          if (!row) return
          e.preventDefault()
          onRowClick?.(row.original)
          break
        }
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [
    enableKeyboardShortcuts,
    focusedIndex,
    moveFocus,
    onRowClick,
    sortedRowIds,
    sortedRows,
    toggleFocusedRow,
  ])

  function handleRowDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = sortedRowIds.indexOf(active.id as string)
    const newIndex = sortedRowIds.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return
    const reordered = arrayMove(sortedRowIds, oldIndex, newIndex)
    setRowOrder(reordered)
    writeStoredOpportunityRowOrder(tenantId, setId, reordered)
    onManualRowReorder?.()
  }

  function handleTableDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over) return

    const activeType = active.data.current?.type
    const overType = over.data.current?.type
    if (activeType !== overType) return

    if (activeType === OPPORTUNITY_TABLE_DRAG_TYPE.row) {
      handleRowDragEnd(event)
      return
    }

    if (activeType === OPPORTUNITY_TABLE_DRAG_TYPE.column) {
      handleHeaderDragEnd(event)
    }
  }

  function handleHeaderDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeId = active.id as string
    const overId = over.id as string
    const pinnedLeft = [...(columnPinning.left ?? [])]
    const activeIsPinned = pinnedLeft.includes(activeId)
    const overIsPinned = pinnedLeft.includes(overId)

    // Reorder within pinned group
    if (activeIsPinned && overIsPinned) {
      const oldIndex = pinnedLeft.indexOf(activeId)
      const newIndex = pinnedLeft.indexOf(overId)
      if (oldIndex !== -1 && newIndex !== -1) {
        setColumnPinning({ ...columnPinning, left: arrayMove(pinnedLeft, oldIndex, newIndex) })
      }
      return
    }

    // Reorder within unpinned columns
    if (!activeIsPinned && !overIsPinned && onColumnOrderChange) {
      const currentOrder = table.getVisibleLeafColumns().map((c) => c.id)
      const oldIndex = currentOrder.indexOf(activeId)
      const newIndex = currentOrder.indexOf(overId)
      if (oldIndex !== -1 && newIndex !== -1) {
        onColumnOrderChange(arrayMove(currentOrder, oldIndex, newIndex))
      }
    }
  }

  if (isLoading) {
    return (
      <OpportunitiesTableFullSkeleton columnCount={jobItems ? 9 : 8} bodyRowCount={5} />
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20">
        <div className="flex size-12 items-center justify-center rounded-full bg-muted">
          <Icon
            name={hasActiveFilters ? "search" : "file"}
            size="lg"
            className="text-muted-foreground/50"
          />
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-sm font-medium text-foreground">
            {hasActiveFilters
              ? t("opportunities.table.emptyFilters")
              : t("opportunities.table.emptyTab")}
          </p>
          {hasActiveFilters && (
            <p className="text-xs text-muted-foreground/60">
              {t("opportunities.table.emptyFiltersHint")}
            </p>
          )}
        </div>
        {hasActiveFilters && onClearFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
          >
            {t("common.clearFilters")}
          </Button>
        )}
      </div>
    )
  }

  const visibleLeafColumns = table.getVisibleLeafColumns()

  return (
    <TooltipProvider delayDuration={400}>
    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleTableDragEnd}>
    <Table
      style={{ width: table.getTotalSize(), minWidth: "100%", tableLayout: "fixed" }}
      data-density={density}
      data-opportunities-table
    >
      <TableHeader className="sticky top-0 z-10 bg-card">
        {table.getHeaderGroups().map((headerGroup) => (
          <SortableContext
            key={headerGroup.id}
            items={headerGroup.headers.map((h) => h.column.id)}
            strategy={horizontalListSortingStrategy}
          >
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort = header.column.getCanSort()
              const sorted = header.column.getIsSorted()
              const align = header.column.columnDef.meta?.align ?? "left"
              const isPinned = header.column.getIsPinned()
              const isSelect = header.column.id === "select"
              const leftPinnedCount = table.getLeftLeafColumns().length
              const isLastLeftPinned =
                isPinned === "left" &&
                header.column.getPinnedIndex() === leftPinnedCount - 1
              const isSelectWithAdjacentPinned = isSelect && leftPinnedCount > 1
              const canPin = !isSelect
              return (
                <DraggableTableHead
                  key={header.id}
                  id={header.column.id}
                  disabled={isSelect}
                  style={{
                    width: header.getSize(),
                    ...(isPinned === "left"
                      ? {
                          position: "sticky",
                          left: header.column.getStart("left"),
                          zIndex: 3,
                          backgroundColor: !isSelect
                            ? "var(--pinned-bg)"
                            : undefined,
                        }
                      : {}),
                  }}
                  className={cn(
                    "relative group bg-card",
                    align === "right" ? "text-right" : "text-left",
                    canSort ? "cursor-pointer select-none" : "",
                    isLastLeftPinned && "shadow-[1px_0_0_0_var(--border-subtle)]",
                    isSelectWithAdjacentPinned && "shadow-[1px_0_0_0_var(--border-subtle)]",
                  )}
                  tabIndex={canSort ? 0 : undefined}
                  onClick={(e) => {
                    if ((e.target as Element).closest("[data-col-menu]")) return
                    if ((e.target as Element).closest("[data-resize-handle]")) return
                    header.column.getToggleSortingHandler()?.(e)
                  }}
                  onKeyDown={canSort ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      header.column.getToggleSortingHandler()?.(e)
                    }
                  } : undefined}
                  aria-sort={canSort ? (sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none") : undefined}
                >
                  {header.isPlaceholder ? null : isSelect ? (
                    <div className="flex w-full items-center justify-center">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 w-full">
                      {/* label + sort — shrinks to make room for menu */}
                      <span className={cn("inline-flex items-center gap-1 min-w-0 flex-1 overflow-hidden", align === "right" && "justify-end")}>
                        {canSort && align === "right" && (
                          <Icon
                            name={sorted === "asc" ? "chevron-up" : sorted === "desc" ? "chevron-down" : "chevrons-up-down"}
                            size="xs"
                            className={cn("shrink-0 transition-opacity", sorted ? "text-primary opacity-100" : "text-muted-foreground/40 opacity-0 group-hover:opacity-60")}
                          />
                        )}
                        <span className="truncate">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {canSort && align !== "right" && (
                          <Icon
                            name={sorted === "asc" ? "chevron-up" : sorted === "desc" ? "chevron-down" : "chevrons-up-down"}
                            size="xs"
                            className={cn("shrink-0 transition-opacity", sorted ? "text-primary opacity-100" : "text-muted-foreground/40 opacity-0 group-hover:opacity-60")}
                          />
                        )}
                      </span>
                      {/* column pin/unpin + menu */}
                      {canPin && (
                        <div
                          data-col-menu
                          className={cn(
                            "shrink-0 transition-opacity",
                            isPinned ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                          )}
                        >
                          {isPinned ? (
                            // Pinned → single click to unpin, no menu needed
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); header.column.pin(false) }}
                                  className="flex h-5 w-5 items-center justify-center rounded text-primary transition-colors hover:bg-muted hover:text-foreground"
                                >
                                  <Icon name="pin-off" size="xs" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>{t("opportunities.table.unpinColumn")}</TooltipContent>
                            </Tooltip>
                          ) : (
                            // Not pinned → menu with pin + unpin all
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted"
                                  aria-label={t("opportunities.table.columnOptions")}
                                >
                                  <Icon name="more-horizontal" size="xs" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-44">
                                <DropdownMenuItem onClick={() => header.column.pin("left")}>
                                  <Icon name="pin" size="xs" className="mr-2" />
                                  {t("opportunities.table.pinColumn")}
                                </DropdownMenuItem>
                                {leftPinnedCount > 1 && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => {
                                      setColumnPinning({ left: ["select"] })
                                    }}>
                                      <Icon name="pin-off" size="xs" className="mr-2" />
                                      {t("opportunities.table.unpinAll")}
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {header.column.getCanResize() && (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={t("opportunities.table.resizeColumn")}
                      tabIndex={0}
                      onPointerDown={(e) => {
                        e.stopPropagation()
                        header.getResizeHandler()(e)
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation()
                        header.getResizeHandler()(e)
                      }}
                      onDoubleClick={() => {
                        // Reset to default size on double-click
                        table.setColumnSizing((prev) => {
                          const next = { ...prev }
                          delete next[header.column.id]
                          return next
                        })
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                          e.preventDefault()
                          const delta = e.key === "ArrowRight" ? 10 : -10
                          const currentSize = header.column.getSize()
                          table.setColumnSizing((prev) => ({
                            ...prev,
                            [header.column.id]: Math.max(header.column.columnDef.minSize ?? 50, currentSize + delta),
                          }))
                        }
                      }}
                      className={`absolute right-0 top-0 z-20 h-full w-4 translate-x-1/2 cursor-col-resize select-none touch-none ${
                        header.column.getIsResizing() ? "" : "group/resize"
                      }`}
                    >
                      <div className={`mx-auto h-full w-0.5 rounded-full transition-colors ${
                        header.column.getIsResizing() ? "bg-primary" : "bg-transparent group-hover/resize:bg-border"
                      }`} />
                    </div>
                  )}
                </DraggableTableHead>
              )
            })}
          </TableRow>
          </SortableContext>
        ))}
      </TableHeader>
      <TableBody>
          <SortableContext items={sortedRows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            {sortedRows.map((row, ri) => (
              <SortableRow
                key={row.id}
                id={row.id}
                ri={ri}
                isSelected={row.getIsSelected()}
                isFocused={focusedRowId === row.id}
                rowRef={(el) => {
                  if (el) rowRefs.current.set(row.id, el)
                  else rowRefs.current.delete(row.id)
                }}
                onClick={() => {
                  setFocusedRowId(row.id)
                  onRowClick?.(row.original)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setFocusedRowId(row.id)
                    onRowClick?.(row.original)
                  } else if (e.key === " ") {
                    e.preventDefault()
                    setFocusedRowId(row.id)
                    toggleFocusedRow(row.id)
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const cellIsPinned = cell.column.getIsPinned()
                  const cellIsSelect = cell.column.id === "select"
                  const cellLeftPinnedCount = table.getLeftLeafColumns().length
                  const cellIsLastLeftPinned =
                    cellIsPinned === "left" &&
                    cell.column.getPinnedIndex() === cellLeftPinnedCount - 1
                  const cellIsSelectWithAdjacentPinned = cellIsSelect && cellLeftPinnedCount > 1
                  return (
                    <TableCell
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                        ...(cellIsPinned === "left"
                          ? {
                              position: "sticky",
                              left: cell.column.getStart("left"),
                              zIndex: 1,
                            }
                          : {}),
                      }}
                      className={cn(
                        // Pinned cells: opaque bg (covers scrolled content) + opaque hover/selected; non-pinned: transparent (row bg shows through)
                        cellIsPinned === "left" && cn(
                          cellIsSelect ? "bg-card" : "bg-[var(--pinned-bg)]",
                          "group-hover/row:bg-[var(--row-hover-bg)] group-data-[state=selected]/row:bg-[var(--row-selected-bg)]",
                        ),
                        cell.column.columnDef.meta?.align === "right" ? "text-right" : "text-left",
                        cell.column.columnDef.meta?.verticalAlign === "top" ? "align-top" : "align-middle",
                        cellIsLastLeftPinned && "shadow-[1px_0_0_0_var(--border-subtle)]",
                        cellIsSelectWithAdjacentPinned && "shadow-[1px_0_0_0_var(--border-subtle)]",
                      )}
                      onClick={cell.column.id === "select" ? (e) => e.stopPropagation() : undefined}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  )
                })}
              </SortableRow>
            ))}
          </SortableContext>
        {isFetchingNextPage && data.length > 0
          ? Array.from({ length: nextPageSkeletonRowCount }).map((_, ri) => (
              <TableRow key={`opportunities-loading-more-${ri}`} aria-busy>
                {visibleLeafColumns.map((col) => (
                  <TableCell key={col.id} style={{ width: col.getSize() }}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : null}
      </TableBody>
    </Table>
    </DndContext>
    </TooltipProvider>
  )
}
